import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface FooterDestination {
  slug: string;
  name: string;
  countrySlug: string;
  countryCode: string;
  experienceCount: number;
}

const CACHE_KEY = "footer_destinations_cache";
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24h

function readLocalCache(): FooterDestination[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function writeLocalCache(data: FooterDestination[]) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // quota exceeded — ignore
  }
}

async function fetchActiveDestinations(): Promise<FooterDestination[]> {
  // Try localStorage first
  const cached = readLocalCache();
  if (cached) return cached;

  const { data, error } = await supabase
    .from("vw_active_cities" as any)
    .select("city_slug, name, country_slug, country_code, experience_count, popularity_score")
    .order("popularity_score", { ascending: false });

  if (error) throw error;

  const rows = ((data ?? []) as unknown) as Array<{
    city_slug: string;
    name: string;
    country_slug: string;
    country_code: string;
    experience_count: number;
    popularity_score: number;
  }>;

  // Sort: popularity desc → experience_count desc → name asc
  rows.sort((a, b) => {
    if (b.popularity_score !== a.popularity_score) return b.popularity_score - a.popularity_score;
    if (b.experience_count !== a.experience_count) return b.experience_count - a.experience_count;
    return a.name.localeCompare(b.name);
  });

  // If > 24 active cities, show top 12
  const limited = rows.length > 24 ? rows.slice(0, 12) : rows;

  const result: FooterDestination[] = limited.map((r) => ({
    slug: r.city_slug,
    name: r.name,
    countrySlug: r.country_slug,
    countryCode: r.country_code,
    experienceCount: r.experience_count,
  }));

  writeLocalCache(result);
  return result;
}

export function useFooterDestinations() {
  return useQuery({
    queryKey: ["footer-destinations"],
    queryFn: fetchActiveDestinations,
    staleTime: 5 * 60 * 1000,
  });
}

export function useFooterCategories() {
  return useQuery({
    queryKey: ["footer-categories"],
    queryFn: async () => {
      const { data } = await supabase
        .from("experiences")
        .select("category")
        .eq("status", "active")
        .not("category", "is", null);
      const cats = [...new Set((data ?? []).map((e) => e.category).filter(Boolean))] as string[];
      return cats.sort();
    },
    staleTime: 5 * 60 * 1000,
  });
}
